<script setup lang="ts">
import NamaLinearProgress from "@/components/progress/NamaLinearProgress.vue";
import ActionPerformerUtil from "@/utils/moreMenu/ActionPerformerUtil.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import Translator from "@/utils/common/Translator.ts";
import {QBtn} from "quasar";
import {computed, ref} from "vue";
import {storeToRefs} from "pinia";
import {useLoginInfoStore} from "@/stores/loginInfo.ts";
import ErrorTooltip from "@/components/EditView/ErrorTooltip.vue";
import {ProgressAction} from "@/utils/presenters/interfaces.ts";
import GUIActionResultType from "@/utils/constants/GUIActionResultType.ts";
import GUIActionResult = namaTypes.GUIActionResult;
import NaMaText = namaTypes.NaMaText;

const props = defineProps<{ result: GUIActionResult; }>();
const {tasks} = storeToRefs(useLoginInfoStore());
const itemResult = computed<GUIActionResult>(() => tasks.value?.find(l => l.pendingResponseId === props.result.pendingResponseId) ?? props.result);
const namaProgressRef = ref();
const action = computed<ProgressAction>(() => {
  if (itemResult.value && itemResult.value.type)
    return {title: itemResult.value.internalURL, icon: calcActionIcon(itemResult.value.type), isActionReady: true};
  return {icon: 'nm-actions', isActionReady: false};
});

function calcActionIcon(type: string): string {
  switch (type) {
    case GUIActionResultType.BarcodePrint:
      return 'qr_code';
    case GUIActionResultType.ExternalURL:
      return 'nm-link';
    case GUIActionResultType.GoToPlace:
      return 'nm-link';
    case GUIActionResultType.InternalURL:
      return 'download_for_offline';
    case GUIActionResultType.NewRecord:
      return 'nm-link';
    case GUIActionResultType.NewRecordInPopup:
      return 'nm-link';
    case GUIActionResultType.PopupListView:
      return 'nm-link';
    case GUIActionResultType.TextToCopy:
      return 'nm-actions';
    case GUIActionResultType.UpdateList:
      return 'nm-actions';
  }
  return 'nm-actions';
}

async function onCancel() {
  await ActionPerformerUtil.killTask(itemResult.value);
}

async function onAction() {
  await ActionPerformerUtil.processAsyncGUIActionResult(itemResult.value);
}

function getErrorMessageFromResult(result: GUIActionResult): NaMaText {
  if (result.failure && result.failure.errors && result.failure.errors[0]?.message)
    return result.failure.errors[0].message;
  if (result.errors && result.errors[0]?.message)
    return result.errors[0].message;
  if (result.messages && result.messages.length > 0)
    return result.messages[0];
  return {resourceId: 'technicalError'};
}
</script>

<template>
  <div class="desktop:tw-w-[550px] tw-w-full tw-h-full tw-flex tw-justify-between tw-border-b-[0.25px] tw-border-gray-300 tw-p-2.5 tw-items-center">
    <div
      class="dark:tw-text-namaFontColor-dark tw-grow group-hover:tw-text-namaFontColor-light group-hover:dark:tw-text-namaFontColor-dark"
      :class="[result.processing?'tw-w-[90%]':'tw-w-[80%]']">
      <NamaLinearProgress ref="namaProgressRef" :pendingResponseId="result.pendingResponseId ?? ''"/>
    </div>
    <div class="tw-flex tw-flex-row tw-gab-4 tw-h-full tw-justify-end"
         :class="[result.processing?'tw-w-[10%]':'tw-w-[20%]']">
      <q-btn v-if="!result.processing && action.title"
             @click="onAction" dense flat :disabled="!action.isActionReady"
             :title="Translator.translateId(action.title)"
             class="nama-custom-btn focus:tw-bg-transparent tw-h-full tw-w-fit">
        <q-avatar square class="tw-size-6">
          <NamaIcon :defaultIcon="action.icon" class="tw-size-6"/>
        </q-avatar>
      </q-btn>
      <q-btn v-if="result.failure || result.errors || result.messageIsError" dense flat
             class="nama-btn-hover nama-custom-btn focus:tw-bg-transparent tw-h-full">
        <q-avatar square class="tw-size-6">
          <NamaIcon defaultIcon="report_gmailerrorred" :hasColor="true" class="tw-fill-red-700"/>
        </q-avatar>
        <ErrorTooltip
          :error-msg="Translator.translateNaMaText(getErrorMessageFromResult(result))"/>
      </q-btn>
      <q-btn v-if="result.processing && !action.title"
             @click="onCancel" dense flat :title="Translator.translateId('killTask')"
             class="nama-custom-btn focus:tw-bg-transparent tw-h-full">
        <q-avatar square class="tw-size-6">
          <NamaIcon defaultIcon="cancel"/>
        </q-avatar>
      </q-btn>
    </div>
  </div>
</template>

<style scoped>

</style>
